// --- Multi-file interface sub-file options (mirrors INTERFACE_FILE_CONFIG in validators.py) ---
// An upload to one of these interfaces has to say WHICH sub-file it is, because each sub-file
// has its own set of columns. Get it wrong and the backend validates the file against every
// sub-file's columns merged together.
export const INTERFACE_FILE_CONFIG = {
    sales:                    ['sales', 'daily_sales'],
    historical_sales:         ['sales', 'daily_sales'],
    delivery_schedules:       ['sl_delivery_schedules', 'pl_delivery_schedules', 'td_delivery_schedules'],
    supplier_closures:        ['supplier_closures', 'supplier_location_closures'],
    constraint_groups:        ['constraint_groups', 'constraints_group_constraints', 'product_location_constraint_groups'],
    category_clusters:        ['clusters', 'cluster_groups', 'cluster_group_cluster', 'location_category_cluster_group_cluster'],
    location_hierarchy:       ['countries', 'chains'],
    location_category_spaces: ['location_category_spaces', 'location_category_space_bays', 'location_category_space_bay_components'],
};

export const getSubFileOptions = (interfaceName) => INTERFACE_FILE_CONFIG[interfaceName] ?? null;

export const autoDetectPrefix = (filename, interfaceName) => {
    const options = getSubFileOptions(interfaceName);
    // Nothing to choose for a single-file interface: send no prefix at all and let the
    // backend infer from the filename, rather than inventing one that matches no source_file.
    if (!options || options.length <= 1) return options?.[0] ?? null;
    // Longest prefix first: a scope segment may now sit between the prefix and the date
    // ({interface}_{scope}_{date}_{id}.csv), so a shorter prefix must not swallow a longer
    // sibling's name as its scope. A scope keeps the source system's own casing, and may
    // also sit at the end, with or without the id ({interface}_{date}_{id}_{scope}.csv).
    const scope = '(_[A-Za-z0-9]+(_[A-Za-z0-9]+)*)?';
    for (const prefix of [...options].sort((a, b) => b.length - a.length)) {
        const escaped = prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const pattern = `^${escaped}${scope}_\\d{4}-\\d{2}-\\d{2}(_\\d+)?${scope}\\.csv$`;
        if (new RegExp(pattern, 'i').test(filename)) return prefix;
    }
    return options[0];
};

/**
 * Re-tags every staged file with a sub-file belonging to `interfaceName`.
 *
 * Files can be staged before an interface is picked, and the interface can be changed while
 * files sit in the list. A prefix left over from another interface matches no source_file at
 * all, so the validator falls back to the union of every sub-file's columns - reporting the
 * other sub-file's mandatory columns as missing while silently accepting its columns.
 *
 * A value the user picked themselves is kept, since it is always one of this interface's own
 * options. Returns `prev` unchanged when nothing moved, so it is safe to call from an effect.
 */
export const reconcileFileSubtypes = (files, interfaceName, prev) => {
    const options = getSubFileOptions(interfaceName);
    const next = {};
    let changed = files.length !== Object.keys(prev).length;

    files.forEach(file => {
        const current = prev[file._uid];
        const belongsHere = options ? options.includes(current) : current == null;
        next[file._uid] = belongsHere ? current : autoDetectPrefix(file.name, interfaceName);
        if (next[file._uid] !== current) changed = true;
    });

    return changed ? next : prev;
};
